/**
 * Certificate of completion — A4 landscape PDF, 100% client-side.
 * Triggered by the home page button when all 11 chapters are completed.
 */

import { jsPDF } from "jspdf";
import { CHAPTERS } from "@/tutorial/chaptersData";

function frenchDate(d = new Date()) {
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function drawSeal(doc, cx, cy, r) {
  // Outer ring
  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(1.4);
  doc.circle(cx, cy, r);
  doc.setLineWidth(0.4);
  doc.circle(cx, cy, r - 2.5);

  // Star points (simple decorative tick marks)
  for (let i = 0; i < 12; i += 1) {
    const a = (i / 12) * Math.PI * 2;
    const x1 = cx + Math.cos(a) * (r - 4);
    const y1 = cy + Math.sin(a) * (r - 4);
    const x2 = cx + Math.cos(a) * (r - 2.5);
    const y2 = cy + Math.sin(a) * (r - 2.5);
    doc.line(x1, y1, x2, y2);
  }

  // Center monogram
  doc.setTextColor(26, 60, 94);
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.text("VP", cx, cy - 1, { align: "center" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(5);
  doc.setTextColor(201, 168, 76);
  doc.text("VITAE-PUBLICA™", cx, cy + 4, { align: "center" });
}

export function exportCertificate({ userName = "Utilisateur" } = {}) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  const W = 297;
  const H = 210;

  // Background
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, H, "F");

  // Double gold border
  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(2);
  doc.rect(8, 8, W - 16, H - 16);
  doc.setLineWidth(0.4);
  doc.rect(12, 12, W - 24, H - 24);

  // Top eyebrow
  doc.setTextColor(26, 60, 94);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("VITAE-PUBLICA™ — Centre d'aide & tutoriel", W / 2, 26, {
    align: "center",
  });

  // Title
  doc.setFont("times", "bold");
  doc.setFontSize(34);
  doc.setTextColor(26, 60, 94);
  doc.text("Certificat de maîtrise", W / 2, 52, { align: "center" });

  // Subtitle
  doc.setFont("helvetica", "italic");
  doc.setFontSize(12);
  doc.setTextColor(120, 120, 120);
  doc.text(
    "VITAE-PUBLICA™ — Analyse vitaliste des finances publiques",
    W / 2,
    62,
    { align: "center" }
  );

  // Body
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(60, 60, 60);
  doc.text(
    `Ceci certifie que ${userName.includes(" ") ? "" : ""}`,
    W / 2,
    82,
    { align: "center" }
  );

  doc.setFont("times", "bold");
  doc.setFontSize(26);
  doc.setTextColor(201, 168, 76);
  doc.text(userName, W / 2, 96, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(60, 60, 60);
  doc.text(
    `a complété avec succès l'intégralité de la formation à VITAE-PUBLICA™,`,
    W / 2,
    110,
    { align: "center" }
  );
  doc.text(
    `soit les ${CHAPTERS.length} chapitres officiels du centre d'aide,`,
    W / 2,
    117,
    { align: "center" }
  );
  doc.text(`en date du ${frenchDate()}.`, W / 2, 124, { align: "center" });

  // Quote band
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  doc.setTextColor(26, 60, 94);
  doc.text(
    "« Les Finances Publiques sont des Tranches de Vies »",
    W / 2,
    144,
    { align: "center" }
  );

  // Seal (bottom-left) + signature line (bottom-right)
  drawSeal(doc, 60, 175, 14);

  doc.setDrawColor(201, 168, 76);
  doc.setLineWidth(0.3);
  doc.line(W - 110, 175, W - 30, 175);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(26, 60, 94);
  doc.text("ELY Mustapha", W - 70, 181, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(
    "PMP · I-PMP · IBM Full Stack Developer",
    W - 70,
    186,
    { align: "center" }
  );

  // Footer mention
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.setFont("helvetica", "italic");
  doc.text("Document pédagogique non officiel", W / 2, H - 14, {
    align: "center",
  });

  const safe = userName.replace(/[^a-zA-Z0-9 _-]/g, "").trim().replace(/\s+/g, "-");
  const filename = `VITAE-PUBLICA_Certificat_${safe || "Utilisateur"}_${new Date()
    .toISOString()
    .slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}
